import * as i0 from '@angular/core';
import { SecurityContext, Injectable, EventEmitter, PLATFORM_ID, Component, Inject, Output, Input, NgModule } from '@angular/core';
import { throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Renderer, marked } from 'marked';
import * as i1 from '@angular/common/http';
import * as i2 from '@angular/platform-browser';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as Prism from 'prismjs';
import { decode } from 'he';
const _c0 = ["*"];
let NgxMdService = /*#__PURE__*/(() => {
  class NgxMdService {
    constructor(_http, _domSanitizer) {
      this._http = _http;
      this._domSanitizer = _domSanitizer;
      this._renderer = new Renderer();
      this.extendRenderer();
      this.setMarkedOptions({});
    }
    // get the content from remote resource
    getContent(path) {
      return this._http.get(path, {
        responseType: 'text'
      }).pipe(map(res => this.extractData(res)), catchError(this.handleError));
    }
    get renderer() {
      return this._renderer;
    }
    // handle data
    extractData(res) {
      return res || '';
    }
    setMarkedOptions(options) {
      options = Object.assign({
        gfm: true,
        tables: true,
        breaks: false,
        pedantic: false,
        sanitize: false,
        smartLists: true,
        smartypants: false
      }, options);
      options.renderer = this._renderer;
      marked.setOptions(options);
    }
    // comple markdown to html
    compile(data, sanitize = true) {
      return this._domSanitizer.sanitize(sanitize ? SecurityContext.HTML : SecurityContext.NONE, marked.parse(data).trim());
    }
    // handle error
    handleError(error) {
      const errMsg = error.message ? error.message : error.toString();
      return throwError(errMsg);
    }
    // extend marked render to support todo checkbox
    extendRenderer() {
      this._renderer.listitem = function (text) {
        if (/^\s*\[[x ]\]\s*/.test(text)) {
          text = text.replace(/^\s*\[ \]\s*/, '<input type="checkbox" class="md-checkbox" disabled> ').replace(/^\s*\[x\]\s*/, '<input type="checkbox" class="md-checkbox" checked disabled> ');
          return '<li style="list-style: none">' + text + '</li>';
        } else {
          return '<li>' + text + '</li>';
        }
      };
    }
    static {
      this.ɵfac = function NgxMdService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMdService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.DomSanitizer));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NgxMdService,
        factory: NgxMdService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NgxMdService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxMdComponent = /*#__PURE__*/(() => {
  class NgxMdComponent {
    constructor(_mdService, _el, platformId) {
      this._mdService = _mdService;
      this._el = _el;
      this.platformId = platformId;
      this.changeLog = [];
      this.error = new EventEmitter();
      this.loaded = new EventEmitter();
      this.rendered = new EventEmitter();
      /**
       * Boolean indicating if the markdown content should be sanitized to avoid script injections
       */
      this.sanitizeHtml = true;
      this._path = '';
      this._data = '';
      this._ext = '';
    }
    set path(value) {
      if (value) {
        this._path = value;
        this.onPathChange();
      }
    }
    set data(value) {
      if (value) {
        this._data = value;
        this.onDataChange(value);
      }
    }
    // on input
    onDataChange(data) {
      if (data) {
        this._el.nativeElement.innerHTML = this._mdService.compile(data, this.sanitizeHtml);
      } else {
        this._el.nativeElement.innerHTML = '';
      }
      this.highlightContent(false);
      this.rendered.emit(data);
    }
    /**
     *  After view init
     */
    ngAfterViewInit() {
      if (this._path) {
        this.onPathChange();
      } else if (!this._data) {
        this.processRaw();
      }
    }
    processRaw() {
      this._md = this.prepare(decode(this._el.nativeElement.innerHTML));
      this.onDataChange(this._md);
    }
    /**
     * get remote conent;
     */
    onPathChange() {
      this._ext = this._path && this._path.split('.').splice(-1).join();
      this._mdService.getContent(this._path).pipe(catchError(this.handleError)).subscribe(data => {
        this._md = this._ext !== 'md' ? '```' + this._ext + '\n' + data + '\n```' : data;
        this.onDataChange(this.prepare(this._md));
        this.loaded.emit(data);
      });
    }
    /**
     * catch http error
     */
    handleError(error, caught) {
      this.error.emit(error);
      console.error('An error occurred', error); // for demo purposes only
      return error.message || error;
    }
    /**
     * Prepare string
     */
    prepare(raw) {
      if (!raw) {
        return '';
      }
      if (this._ext === 'md' || !this.path) {
        let isCodeBlock = false;
        return raw.split('\n').map(line => {
          // If the first non-blank chars are an opening/closing code block, toggle the flag
          if (this.trimLeft(line).substring(0, 3) === '```') {
            isCodeBlock = !isCodeBlock;
          }
          return isCodeBlock ? line : line.trim();
        }).join('\n');
      }
      return raw.replace(/\"/g, '\'');
    }
    /**
     * Trim left whitespace
     */
    trimLeft(line) {
      return line.replace(/^\s+|\s+$/g, '');
    }
    /**
     * Use Prism to highlight code snippets only on the browser
     */
    highlightContent(async) {
      if (isPlatformBrowser(this.platformId)) {
        Prism.highlightAll(async);
      }
    }
    static {
      this.ɵfac = function NgxMdComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMdComponent)(i0.ɵɵdirectiveInject(NgxMdService), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(PLATFORM_ID));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgxMdComponent,
        selectors: [["markdown"], ["", "Markdown", ""], ["ngx-md"], ["", "NgxMd", ""]],
        inputs: {
          path: "path",
          data: "data",
          sanitizeHtml: "sanitizeHtml"
        },
        outputs: {
          error: "error",
          loaded: "loaded",
          rendered: "rendered"
        },
        standalone: false,
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NgxMdComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        styles: [".token.operator[_ngcontent-%COMP%], .token.entity[_ngcontent-%COMP%], .token.url[_ngcontent-%COMP%], .language-css[_ngcontent-%COMP%]   .token.string[_ngcontent-%COMP%], .style[_ngcontent-%COMP%]   .token.string[_ngcontent-%COMP%]{background:none}.md-checkbox[_ngcontent-%COMP%]{vertical-align:middle;margin:0 .2em .25em -1.6em;font-size:16px}"]
      });
    }
  }
  return NgxMdComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxMdConfig = /*#__PURE__*/(() => {
  class NgxMdConfig {
    static {
      this.ɵfac = function NgxMdConfig_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMdConfig)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NgxMdConfig,
        factory: NgxMdConfig.ɵfac
      });
    }
  }
  return NgxMdConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxMdModule = /*#__PURE__*/(() => {
  class NgxMdModule {
    static forRoot() {
      return {
        ngModule: NgxMdModule,
        providers: [NgxMdConfig]
      };
    }
    static {
      this.ɵfac = function NgxMdModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMdModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxMdModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [NgxMdService],
        imports: [CommonModule]
      });
    }
  }
  return NgxMdModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-md
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxMdComponent, NgxMdModule, NgxMdService };
